import { useMemo } from 'react';
import type { RootState } from '@/store';
import { glassVariants } from '@/styles/liquidGlass';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getTutorialForPath } from '@/config/tutorialRegistry';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { toggleGuidedMode } from '@/store/slices/guidedModeSlice';

type GuidedTutorialPanelProps = {
  /** Distance from viewport bottom to leave room for chat FAB + help toggle */
  bottomOffsetPx: number;
};

export const GuidedTutorialPanel = ({
  bottomOffsetPx,
}: GuidedTutorialPanelProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const guidedMode = useSelector((s: RootState) => s.guidedMode.guidedMode);
  const { data: user } = useGetCurrentUserQuery();

  const entry = useMemo(
    () => getTutorialForPath(pathname, user?.role),
    [pathname, user?.role]
  );

  const open = Boolean(guidedMode && entry);
  const glass = glassVariants.bold;

  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          left: { xs: theme.spacing(2), sm: '75%' },
          right: theme.spacing(2),
          bottom: bottomOffsetPx,
          zIndex: 999,
          maxHeight: isMobile ? '32vh' : '28vh',
          overflow: 'auto',
          p: 2,
          borderRadius: `${theme.shape.borderRadius}px`,
          background: glass.background,
          backgroundImage: 'none',
          border: glass.border,
          backdropFilter: glass.backdropFilter,
          WebkitBackdropFilter: glass.backdropFilter,
          boxShadow: glass.boxShadow,
          color: theme.palette.text.primary,
        }}
      >
        {entry && (
          <>
            {/* The panel floats over page content; let people close it
                (guided mode can be turned back on from the help toggle). */}
            <IconButton
              aria-label="Close guided help"
              size="small"
              onClick={() => dispatch(toggleGuidedMode())}
              sx={{ position: 'absolute', top: 4, right: 4 }}
            >
              <i className="fa-light fa-xmark" />
            </IconButton>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              gutterBottom
              sx={{ color: theme.palette.primary.main, pr: 4 }}
            >
              {entry.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 1.5 }}
            >
              {entry.summary}
            </Typography>
            <List dense disablePadding>
              {entry.bullets.map((text, i) => (
                <ListItem
                  key={i}
                  disableGutters
                  sx={{ alignItems: 'flex-start', py: 0.25 }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 28,
                      mt: 0.25,
                      color: theme.palette.primary.main,
                    }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={700}
                    >
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { color: theme.palette.text.secondary },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>
    </Collapse>
  );
};
